function readinessTone(status) {
  if (status === "ready") {
    return "ready";
  }
  if (status === "attention_required") {
    return "overdue";
  }
  return "stale";
}

export function buildCustomerReleasePack(controlCenter, options = {}) {
  const role = options.role || "operator";
  const onboarding = controlCenter.onboarding || {};
  const readinessChecks = controlCenter.readiness_checks || [];
  const queueSla = controlCenter.queue_sla || {};
  const smoke = controlCenter.smoke_governance || {};
  const maintenanceRuns = (controlCenter.maintenance || {}).runs || [];
  const readyChecks = readinessChecks.filter((item) => item.status === "ready").length;
  const launchReady =
    onboarding.status === "ready" &&
    readinessChecks.every((item) => item.status === "ready") &&
    smoke.rotation_status === "ready";

  const checklistRows = [
    {
      label: "Onboarding status",
      value: onboarding.status || "unknown",
      status: onboarding.status === "ready" ? "ready" : "stale",
      meta: onboarding.next_step_summary || "Complete the remaining onboarding checklist steps.",
    },
    {
      label: "Readiness checks",
      value: `${readyChecks}/${readinessChecks.length || 1} ready`,
      status: readyChecks === readinessChecks.length && readinessChecks.length > 0 ? "ready" : "stale",
      meta: "Queue policy, automation, reporting, and smoke posture should all be acknowledged before launch.",
    },
    {
      label: "Smoke rotation",
      value: smoke.rotation_status || "unknown",
      status: smoke.rotation_status === "ready" ? "ready" : "stale",
      meta: smoke.recommended_action || "Review the canonical smoke account before broader rollout.",
    },
    {
      label: "Live queue pressure",
      value: queueSla.overdue_total || 0,
      status: (queueSla.overdue_total || 0) > 0 ? "overdue" : "ready",
      meta: `${queueSla.first_response_overdue_total || 0} first-response overdue while preparing launch.`,
    },
    {
      label: "Maintenance watch",
      value: maintenanceRuns[0]?.status || "unknown",
      status: maintenanceRuns[0]?.status === "completed" ? "ready" : "stale",
      meta: maintenanceRuns[0]?.run_kind || "No maintenance run available yet.",
    },
  ];

  const roleCards = [
    {
      key: "operator",
      title: "Operator daily loop",
      summary: "Use Inbox Workspace, open the next conversation, reply, and record disposition from one surface.",
      bullets: [
        "Start on Operator for guided triage and queue ownership.",
        "Work from the Conversation Detail panel so replies and dispositions refresh immediately.",
        "Use saved inbox views to keep the right slice in focus during the day.",
      ],
    },
    {
      key: "manager",
      title: "Manager command loop",
      summary: "Review queue pressure, drill into risk slices, and rebalance with bulk controls.",
      bullets: [
        "Use Manager for spotlight cards, workload inspection, and recent action review.",
        "Move directly from risk drilldowns into reversible bulk actions.",
        "Use Governance and Support when queue pressure or incidents need escalation.",
      ],
    },
    {
      key: "tenant_owner",
      title: "Owner launch loop",
      summary: "Review readiness, finalize settings, and verify smoke/governance before wider rollout.",
      bullets: [
        "Use Admin to validate onboarding, queue policy, automation, alerts, and digests.",
        "Keep the smoke account current and review governance before customer-facing changes.",
        "Run the production smoke pack after workflow-critical releases.",
      ],
    },
  ].map((item) => ({
    ...item,
    active: item.key === role || (role === "manager" && item.key === "tenant_owner"),
  }));

  const docsRows = [
    {
      title: "Customer quickstart",
      detail: "Owner-facing setup overview covering sign-in, onboarding, queue policy, and go-live validation.",
      status: "ready",
    },
    {
      title: "Daily operations guide",
      detail: "Operator, manager, and owner routines for running the CRM without API calls.",
      status: "ready",
    },
    {
      title: "Release checklist",
      detail: "Workflow-critical release gate: acceptance, smoke, and production verification for the CRM webapp.",
      status: "ready",
    },
    {
      title: "Support playbook",
      detail: "Escalation and recovery path for smoke drift, worker issues, delivery failures, and rollout support.",
      status: "ready",
    },
    {
      title: "Role walkthroughs",
      detail: "Operator, manager, and owner walkthroughs for onboarding and first-week CRM usage.",
      status: "ready",
    },
    {
      title: "Browser validation",
      detail: "Validated desktop Edge, desktop Chrome, and phone-sized CRM viewport checks.",
      status: "ready",
    },
  ];

  const browserRows = [
    {
      label: "Desktop Edge",
      value: "validated",
      status: "ready",
      meta: "Playwright desktop project covering the full CRM acceptance flow.",
    },
    {
      label: "Desktop Chrome",
      value: "validated",
      status: "ready",
      meta: "Secondary Chromium-family check for the customer rollout browser matrix.",
    },
    {
      label: "Mobile viewport",
      value: "validated",
      status: "ready",
      meta: "Phone-sized CRM acceptance pass covering operator and admin workflows.",
    },
  ];

  const deploymentRows = [
    {
      label: "Release SHA gate",
      value: "required",
      status: "ready",
      meta: "Always wait for /health to report the expected SHA before declaring the rollout complete.",
    },
    {
      label: "Milestone smoke",
      value: "required",
      status: "ready",
      meta: "Run the milestone-specific smoke whenever the release pack or workflow-critical UI changes.",
    },
    {
      label: "Full owner smoke",
      value: "required",
      status: "ready",
      meta: "Run the owner smoke pack after the milestone smoke to validate the complete CRM workflow set.",
    },
    {
      label: "Support handoff",
      value: "documented",
      status: "ready",
      meta: "Use the support playbook and role walkthrough docs for launch-day escalation and customer onboarding.",
    },
  ];

  return {
    launchTone: launchReady ? "ready" : "stale",
    headline: launchReady ? "Customer-ready" : "Final rollout pass in progress",
    headlineDetail: launchReady
      ? "The CRM shell, operator workflow, manager controls, and owner governance surfaces are ready for broader customer-facing use."
      : "Use this pack to finish copy review, operational readiness, and rollout verification before broader customer launch.",
    checklistRows,
    roleCards,
    docsRows,
    browserRows,
    deploymentRows,
  };
}
